import React, { useState } from 'react';
import { RoundingList } from './RoundingList';
import { RoundingForm } from './RoundingForm';
import { BookIcon, ChevronRightIcon, CircleDotIcon } from 'lucide-react';
export type RoundingRule = {
  id: string;
  name: string;
  description: string;
  roundingType: 'Nearest' | 'Up' | 'Down' | 'Swedish' | 'Truncate';
  interval: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};
export function RoundingConfiguration({
  locale = 'en-US'
}) {
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<RoundingRule | null>(null);
  const [roundingRules, setRoundingRules] = useState<RoundingRule[]>([{
    id: 'round-001',
    name: 'Standard Cents',
    description: 'Round to nearest cent (0.01)',
    roundingType: 'Nearest',
    interval: 0.01,
    active: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'round-002',
    name: 'Nickel Rounding',
    description: 'Round up to nearest 0.05',
    roundingType: 'Up',
    interval: 0.05,
    active: true,
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2023-01-15T00:00:00Z'
  }, {
    id: 'round-003',
    name: 'Whole Dollar',
    description: 'Round down to nearest dollar',
    roundingType: 'Down',
    interval: 1.0,
    active: true,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }, {
    id: 'round-004',
    name: 'Swedish Cash Rounding',
    description: 'Swedish rounding for cash transactions',
    roundingType: 'Swedish',
    interval: 0.5,
    active: false,
    createdAt: '2023-02-15T00:00:00Z',
    updatedAt: '2023-03-01T00:00:00Z'
  }, {
    id: 'round-005',
    name: 'Truncate to Penny',
    description: 'Truncate to penny without rounding',
    roundingType: 'Truncate',
    interval: 0.01,
    active: true,
    createdAt: '2023-03-01T00:00:00Z',
    updatedAt: '2023-03-01T00:00:00Z'
  }]);
  const handleAddRule = () => {
    setEditingRule(null);
    setShowForm(true);
  };
  const handleEditRule = (rule: RoundingRule) => {
    setEditingRule(rule);
    setShowForm(true);
  };
  const handleSaveRule = (rule: Omit<RoundingRule, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingRule) {
      // Update existing rule
      setRoundingRules(roundingRules.map(r => r.id === editingRule.id ? {
        ...editingRule,
        ...rule,
        updatedAt: new Date().toISOString()
      } : r));
    } else {
      // Create new rule
      const newRule: RoundingRule = {
        id: `round-${String(roundingRules.length + 1).padStart(3, '0')}`,
        ...rule,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setRoundingRules([...roundingRules, newRule]);
    }
    setShowForm(false);
    setEditingRule(null);
  };
  const handleToggleActive = (id: string) => {
    setRoundingRules(roundingRules.map(rule => rule.id === id ? {
      ...rule,
      active: !rule.active,
      updatedAt: new Date().toISOString()
    } : rule));
  };
  const handleDeleteRule = (id: string) => {
    setRoundingRules(roundingRules.filter(rule => rule.id !== id));
  };
  const handleCancelForm = () => {
    setShowForm(false);
    setEditingRule(null);
  };
  return <div data-id="element-8600">
      <div className="mb-8" data-id="element-8601">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-8602">
          Rounding Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-8603">
          Define reusable rounding rules used in modules such as Invoicing,
          Purchase Orders, and Budget
        </p>
      </div>
      {/* Breadcrumbs */}
      <nav className="flex mb-6" aria-label="Breadcrumb" data-id="element-8604">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-8605">
          <li className="inline-flex items-center" data-id="element-8606">
            <span className="inline-flex items-center text-sm font-medium text-indigo-600" data-id="element-8607">
              <BookIcon className="w-4 h-4 mr-2" data-id="element-8608" />
              Financial
            </span>
          </li>
          <li data-id="element-8609">
            <div className="flex items-center" data-id="element-8610">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-8611" />
              <span className="inline-flex items-center text-sm font-medium text-indigo-600" data-id="element-8612">
                <CircleDotIcon className="w-4 h-4 mr-2" data-id="element-8613" />
                Rounding
              </span>
            </div>
          </li>
        </ol>
      </nav>
      {showForm ? <RoundingForm rule={editingRule} onSave={handleSaveRule} onCancel={handleCancelForm} data-id="element-8614" /> : <RoundingList rules={roundingRules} onAdd={handleAddRule} onEdit={handleEditRule} onToggleActive={handleToggleActive} onDelete={handleDeleteRule} locale={locale} data-id="element-8615" />}
    </div>;
}